import React, { useMemo } from "react";

import {
  getCanonicalShiftStatus,
  getAssignedWorkerUid,
  getShiftStartMs,
  getShiftEndMs,
} from "../../../core/shifts/shiftLifecycle";
import {
  getShiftFinancialSnapshot,
} from "../../../core/shifts/shiftFinancials";
import { buildFacility360Metrics } from "../utils/facility360Metrics";

function normalize(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function MetricCard({
  label,
  value,
  detail,
  color = "#0f172a",
}) {
  return (
    <div
      style={{
        padding: 14,
        borderRadius: 14,
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
      }}
    >
      <div
        style={{
          color: "#64748b",
          fontSize: 10,
          fontWeight: 900,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          color,
          fontSize: 22,
          fontWeight: 950,
        }}
      >
        {value}
      </div>

      {detail ? (
        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 10,
            fontWeight: 750,
          }}
        >
          {detail}
        </div>
      ) : null}
    </div>
  );
}

export default function Facility360Overview({
  facility,
  shifts = [],
  users = [],
}) {

const sharedMetrics = useMemo(
  () =>
    buildFacility360Metrics({
      facility,
      shifts,
      users,
    }),
  [facility, shifts, users]
);

const metrics = {
  totalShifts:
    sharedMetrics.totalShiftCount,
  openCount: sharedMetrics.openCount,
  coveredCount:
    sharedMetrics.activeCount +
    sharedMetrics.completedCount,
  activeCount: sharedMetrics.activeCount,
  completedCount:
    sharedMetrics.completedCount,
  cancelledCount:
    sharedMetrics.cancelledCount,
  totalHours:
    sharedMetrics.completedHours,
  coverage: sharedMetrics.coverage,
  workforceCount:
    sharedMetrics.workforceCount,
  activeWorkerCount:
    sharedMetrics.readyWorkerCount,
  roleCounts: sharedMetrics.roleCounts,
};

  const facilityName =
    facility?.name ||
    facility?.facilityName ||
    facility?.id ||
    "Facility";

  const location = [
    facility?.city,
    facility?.state,
  ]
    .filter(Boolean)
    .join(", ");

  const status = normalize(
    facility?.status || "active"
  );

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div
        style={{
          padding: 18,
          borderRadius: 18,
          color: "#ffffff",
          background:
            "linear-gradient(135deg, #0f172a 0%, #0369a1 100%)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 24,
                fontWeight: 950,
              }}
            >
              {facilityName}
            </div>

            <div
              style={{
                marginTop: 5,
                color: "#bae6fd",
                fontSize: 13,
                fontWeight: 800,
              }}
            >
              {location ||
                facility?.address ||
                "Location not available"}
            </div>

            <div
              style={{
                marginTop: 4,
                color: "#e0f2fe",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {facility?.tenantName ||
                facility?.tenantId ||
                "Tenant not available"}
            </div>
          </div>

          <div
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              color:
                status === "active"
                  ? "#166534"
                  : "#92400e",
              background:
                status === "active"
                  ? "#dcfce7"
                  : "#fef3c7",
              fontSize: 10,
              fontWeight: 950,
              textTransform: "uppercase",
            }}
          >
            {status}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(2, minmax(0, 1fr))",
          gap: 9,
        }}
      >
        <MetricCard
          label="Coverage"
          value={`${metrics.coverage}%`}
          detail="Covered versus open demand"
          color="#166534"
        />

        <MetricCard
          label="Open Shifts"
          value={metrics.openCount}
          detail="Currently unfilled"
          color={
            metrics.openCount > 0
              ? "#b45309"
              : "#166534"
          }
        />

        <MetricCard
          label="Active Shifts"
          value={metrics.activeCount}
          detail="Assigned through arrived"
          color="#1d4ed8"
        />

        <MetricCard
          label="Completed"
          value={metrics.completedCount}
          detail="Recorded completed shifts"
          color="#166534"
        />

        <MetricCard
          label="Workforce"
          value={metrics.workforceCount}
          detail={`${metrics.activeWorkerCount} active or ready`}
          color="#7c3aed"
        />

        <MetricCard
          label="Completed Hours"
          value={metrics.totalHours.toFixed(1)}
          detail="Calculated from completed shifts"
          color="#0369a1"
        />
      </div>

      <div
        style={{
          padding: 14,
          borderRadius: 16,
          border: "1px solid #e2e8f0",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            color: "#0f172a",
            fontSize: 14,
            fontWeight: 950,
          }}
        >
          Workforce by Role
        </div>

        <div
          style={{
            marginTop: 10,
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          {Object.keys(metrics.roleCounts)
            .length === 0 ? (
            <div
              style={{
                color: "#64748b",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              No workforce is currently assigned.
            </div>
          ) : (
            Object.entries(
              metrics.roleCounts
            ).map(([role, count]) => (
              <div
                key={role}
                style={{
                  padding: "7px 10px",
                  borderRadius: 999,
                  color: "#1d4ed8",
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  fontSize: 11,
                  fontWeight: 900,
                }}
              >
                {role}: {count}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}